const clientPortalPathPrefix = (): string =>
  process.env.REACT_APP_CLIENT_PORTAL ?? "clientportal";

const sessionQuery = (sid: string) =>
  `SessionID=${encodeURIComponent(sid)}`;

export async function listFirmRetainerFiles(
  sid: string,
  province: string
): Promise<string[]> {
  const res = await fetch(
    `/${clientPortalPathPrefix()}/lawyer/${province}/shell/firmRetainers?${sessionQuery(sid)}`,
    { credentials: "include" }
  );
  if (!res.ok) {
    throw new Error(`Failed to list retainers (${res.status})`);
  }
  const data = (await res.json()) as { ok?: boolean; files?: string[]; error?: string };
  if (!data.ok) {
    throw new Error(data.error || "List failed");
  }
  return data.files ?? [];
}

export async function downloadFirmRetainerFile(
  sid: string,
  province: string,
  fileName: string
): Promise<ArrayBuffer> {
  const enc = encodeURIComponent(fileName);
  const res = await fetch(
    `/${clientPortalPathPrefix()}/lawyer/${province}/shell/firmRetainers/${enc}?${sessionQuery(sid)}`,
    { credentials: "include" }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || `Download failed (${res.status})`);
  }
  return await res.arrayBuffer();
}

export async function uploadFirmRetainerFile(
  sid: string,
  province: string,
  fileName: string,
  buffer: ArrayBuffer,
  options?: { overwrite?: boolean }
): Promise<void> {
  const enc = encodeURIComponent(fileName);
  const overwriteQs =
    options?.overwrite === true ? "&overwrite=1" : "";
  const res = await fetch(
    `/${clientPortalPathPrefix()}/lawyer/${province}/shell/firmRetainers/${enc}?${sessionQuery(sid)}${overwriteQs}`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/octet-stream" },
      body: buffer,
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || `Upload failed (${res.status})`);
  }
}

export async function deleteFirmRetainerFile(
  sid: string,
  province: string,
  fileName: string
): Promise<void> {
  const enc = encodeURIComponent(fileName);
  const res = await fetch(
    `/${clientPortalPathPrefix()}/lawyer/${province}/shell/firmRetainers/${enc}?${sessionQuery(sid)}`,
    { method: "DELETE", credentials: "include" }
  );
  if (!res.ok && res.status !== 404) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || `Delete failed (${res.status})`);
  }
}

export async function renameFirmRetainerFile(
  sid: string,
  province: string,
  fromFileName: string,
  toFileName: string
): Promise<void> {
  const res = await fetch(
    `/${clientPortalPathPrefix()}/lawyer/${province}/shell/firmRetainers/rename?${sessionQuery(sid)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fromFileName, toFileName }),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || `Rename failed (${res.status})`);
  }
}
